import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import {
  messages,
  ALL_EVENT,
  EMPTY_STRING,
  localStorageKeys,
  ADD_RECAPZ_STEPS,
  RECAP_FORM_FIELDS,
  eventBookingStatus,
  eventBookingConstants,
} from "../../../../constants";
import AiSummaryRecap from "../AddRecapz/AiSummaryRecap";
import SelectRecapType from "../AddRecapz/SelectRecapType";
import { eventBookingActions } from "../../../../redux/actions";
import SelectEventQuantity from "../AddRecapz/SelectEventQuantity";
import BackButton from "../../../../shared/components/IconButton/BackButton";
import { getLocalStorageItem, saveToLocalStorage } from "../../../../system";
import {
  customToast,
  filterByStatus,
  removeDraftEvents,
  buildRecapBriefPayload,
  hasEmptyReelDetailDescription,
  safeJsonParse,
} from "../../../../shared";

const AddRecapz = (props) => {
  const { t } = useTranslation("common");

  const { addRecapz, onNext } = props;

  const [activeStep, setActiveStep] = useState(() => {
    return Number(
      getLocalStorageItem(
        localStorageKeys.ADD_RECAPZ_ACTIVE_STEP,
        ADD_RECAPZ_STEPS.ADD_BOOKING
      )
    );
  });
  const [currentContent, setCurrentContent] = useState(EMPTY_STRING);

  useEffect(() => {
    window.scrollTo(0, 0);
    saveToLocalStorage(localStorageKeys.ADD_RECAPZ_ACTIVE_STEP, activeStep);
    // eslint-disable-next-line
  }, [activeStep]);

  useEffect(() => {
    if (props.activeTab === eventBookingConstants.TABS_ID.ADD_REEL) {
      let updatedAddRecapz = [...addRecapz];

      // Only 1 event (remove All Event reel)
      if (
        !props.isEditEvent &&
        removeDraftEvents(
          filterByStatus(props.formState ?? [], [eventBookingStatus.CANCEL])
        ).length === 1
      ) {
        updatedAddRecapz = updatedAddRecapz.filter(
          (reel) => reel.eventId !== ALL_EVENT.id
        );
      }

      // Remove recapz for draft or canceled events
      const excludedEventIds = props.formState
        .filter(
          (event) =>
            event.isSaveAsDraft || event.status === eventBookingStatus.CANCEL
        )
        .map((event) => event.id);

      if (excludedEventIds.length > 0) {
        updatedAddRecapz = updatedAddRecapz.filter(
          (reel) => !excludedEventIds.includes(reel.eventId)
        );
      }

      const eventNameMap = new Map(
        props.formState?.map((event) => [event.id, event.eventName])
      );

      // Update eventName from formState
      updatedAddRecapz = updatedAddRecapz.map((reel) => {
        const eventName =
          eventNameMap.get(reel.eventId) ??
          (reel.eventId === -1 ? ALL_EVENT.name : reel.eventName);

        return {
          ...reel,
          eventName,
          reelDetail: reel?.reelDetail?.map((detail, index) => ({
            ...detail,
            reelName: `${eventName} - Recap ${index + 1}`,
          })),
        };
      });

      // Set updated recapz if something changed
      if (JSON.stringify(updatedAddRecapz) !== JSON.stringify(addRecapz)) {
        props.setAddRecapz(updatedAddRecapz);
      }
    }
    // eslint-disable-next-line
  }, [props.activeTab, props.formState]);

  useEffect(() => {
    if (
      addRecapz.length === 0 &&
      activeStep !== ADD_RECAPZ_STEPS.SELECT_EVENTS_QUANTITY
    ) {
      setActiveStep(ADD_RECAPZ_STEPS.ADD_BOOKING);
    }
    // eslint-disable-next-line
  }, [addRecapz]);

  const recapBriefs = useMemo(() => {
    return addRecapz?.map(recap => {
      const reelDetail = recap.reelDetail?.[0] ?? {}; // Fallback to empty object

      return {
        ...reelDetail,
        eventName: recap.eventName || "", // Safely attach event name
      };
    }) ?? [];
  }, [addRecapz]);

  useEffect(() => {
    const currentDescription = recapBriefs;
    setCurrentContent(currentDescription);
  }, [recapBriefs]);

  const handleSkip = () => {
    props.setAddRecapz([]);
    setActiveStep(ADD_RECAPZ_STEPS.ADD_BOOKING);
    onNext();
  };
  const handlePrevious = () => {
    props.onPrevious();
  };

  const handleActiveStep = (step) => {
    setActiveStep(step);
  };

  const generateEditBrief = () => {
    let dataToSend = [];

    // Case 1: recaps tied to formState events
    dataToSend = props.formState.reduce((acc, event) => {
      const matchedRecap = addRecapz.find(
        (recap) => recap.eventId === event.id
      );
      if (matchedRecap) {
        acc.push(buildRecapBriefPayload(matchedRecap, props.formState, event));
      }
      return acc;
    }, []);

    // Case 2: combined reels not tied to formState
    addRecapz
      .filter((recap) => recap.isCombinedEventsReel === true)
      .forEach((combined) => {
        dataToSend.push(
          buildRecapBriefPayload(combined, props.formState, null)
        );
      });

    props.getEditBrief(dataToSend, onResponseCallback);
  };

  const onResponseCallback = (response) => {
    if (response) {
      // Safely parse AI response into JSON, fallback to empty array if invalid JSON
      const parsed = safeJsonParse(response, []);
      const recapDescriptionMap = parsed.reduce((acc, item) => {
        const key = Object.keys(item)[0];
        const value = item[key];

        // merge text if key already exists, otherwise assign
        acc[key] = acc[key] ? acc[key] + " " + value : value;
        return acc;
      }, {});

      const updatedAddRecapz = addRecapz.map((recap) => ({
        ...recap,
        reelDetail:
          recap.reelDetail?.map((detail) => ({
            ...detail,
            [RECAP_FORM_FIELDS.DESCRIPTION]: recapDescriptionMap[recap.eventId === -1 ? -1 : recap.eventName] ?? ""
          })) || [],
      }));

      props.setAddRecapz(updatedAddRecapz);
      handleActiveStep(ADD_RECAPZ_STEPS.RECAP_FORM);
    }
  };

  const handleBriefChange = (content, index) => {
    const updatedAddRecapz = addRecapz.map((recap, i) => {
      if (i === index) {
        return {
          ...recap,
          reelDetail: recap.reelDetail?.map(detail => ({
            ...detail,
            [RECAP_FORM_FIELDS.DESCRIPTION]: content?.[RECAP_FORM_FIELDS.DESCRIPTION], //update only selected recap description
          })) || [],
        };
      }
      return recap; //keep others unchanged
    });

    props.setAddRecapz(updatedAddRecapz);
  };

  const handleBriefSubmit = () => {
    if (hasEmptyReelDetailDescription(addRecapz)) {
      customToast(messages.EDIT_BRIEF_MESSAGE, toast.TYPE.ERROR);
      return;
    }
    onNext();
  };

  return (
    <>
      <div
        className={`add-recapz-container mt-4 mx-3 ${activeStep === ADD_RECAPZ_STEPS.RECAP_FORM
            ? "add-recapz-custom"
            : EMPTY_STRING
          }`}
      >
        <div className="row">
          {activeStep === ADD_RECAPZ_STEPS.ADD_BOOKING && (
            <div className="add-booking col-12 col-md-8 col-lg-6 margin-0-auto">
              <p className="add-recapz-heading label-content-600 mb-2">
                would you like to add video edit(s) for your bookings?
              </p>
              <p className="add-recapz-description mb-0">
                let our editors turn your shutterb captured video clips into an event recap starting at $108/edit.
              </p>
              <div className="mt-4">
                <button
                  type="button"
                  className="button-common button-fill yes-btn mb-3 mb-lg-0"
                  onClick={() =>
                    handleActiveStep(ADD_RECAPZ_STEPS.SELECT_EVENTS_QUANTITY)
                  }
                >
                  {t("yes")}
                </button>
                <button
                  type="button"
                  className="button-common button-outline ml-4 mb-3 mb-lg-0"
                  onClick={handleSkip}
                >
                  {t("skip")}
                </button>
              </div>
            </div>
          )}
          {activeStep === ADD_RECAPZ_STEPS.SELECT_EVENTS_QUANTITY && (
            <SelectEventQuantity
              handleActiveStep={handleActiveStep}
              formState={removeDraftEvents(
                filterByStatus(props.formState ?? [], [
                  eventBookingStatus.CANCEL,
                ])
              )}
            />
          )}
          {activeStep === ADD_RECAPZ_STEPS.SELECT_RECAP_TYPE && (
            <SelectRecapType
              handleActiveStep={handleActiveStep}
              generateEditBrief={generateEditBrief}
            />
          )}
          {activeStep === ADD_RECAPZ_STEPS.RECAP_FORM && (
            <AiSummaryRecap
              currentRecaps={recapBriefs}
              handleBriefChange={handleBriefChange}
              handleValidSubmit={handleBriefSubmit}
              handleGoBack={() =>
                handleActiveStep(ADD_RECAPZ_STEPS.SELECT_RECAP_TYPE)
              }
              generateEditBrief={generateEditBrief}
              currentContent={currentContent}
              setCurrentContent={setCurrentContent}
            />
          )}
        </div>

        <BackButton
          className="event-back-button"
          onClick={handlePrevious}
          text={`${props.previousActiveTab}`}
        />
      </div>
    </>
  );
};

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  addRecapz: state.eventBooking.addRecapz,
  formState: state.eventBooking.formState,
  isEditEvent: state.eventBooking.isEditEvent,
  previousActiveTab: state.eventBooking.previousActiveTab,
});

// Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  setAddRecapz: (data) => dispatch(eventBookingActions.setAddRecapz(data)),
  getEditBrief: (data, onResponseCallback) =>
    dispatch(eventBookingActions.getEditBrief(data, onResponseCallback)),
});

// mapping action and store the function via props
export default connect(mapStateToProps, mapDispatchToProps)(AddRecapz);
